import { useState } from 'react'
import type { QuestionCategory, Question, UserAnswer } from '../lib/types'

interface Props {
  categories: QuestionCategory[]
  questions: Question[]
  answers: UserAnswer[]
  onSaveAnswer: (questionId: string, answer: string, rating?: number) => Promise<void>
}

export default function QuestionList({ categories, questions, answers, onSaveAnswer }: Props) {
  const [expandedCat, setExpandedCat] = useState<string | null>(categories[0]?.id || null)
  const [editingQ, setEditingQ] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)

  const answerMap = new Map(answers.map(a => [a.question_id, a]))

  const catQuestions = (catId: string) =>
    questions.filter(q => q.category_id === catId).sort((a, b) => a.sort_order - b.sort_order)

  const answeredCount = (catId: string) =>
    catQuestions(catId).filter(q => answerMap.has(q.id) && answerMap.get(q.id)!.answer).length

  const handleSave = async (questionId: string) => {
    setSaving(true)
    try {
      await onSaveAnswer(questionId, draft)
      setEditingQ(null)
      setDraft('')
    } finally {
      setSaving(false)
    }
  }

  const startEdit = (q: Question) => {
    setEditingQ(q.id)
    setDraft(answerMap.get(q.id)?.answer || '')
  }

  const ICONS: Record<string, string> = {
    cpu: '💻', handshake: '🤝', wrench: '🔧', chart: '📊', rocket: '🚀',
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500 mb-4">
        {answers.filter(a => a.answer).length} of {questions.length} questions answered
      </p>

      {categories.sort((a, b) => a.sort_order - b.sort_order).map(cat => {
        const qs = catQuestions(cat.id)
        const answered = answeredCount(cat.id)
        const isExpanded = expandedCat === cat.id

        return (
          <div key={cat.id} className="card p-0 overflow-hidden">
            <button
              onClick={() => setExpandedCat(isExpanded ? null : cat.id)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{ICONS[cat.icon || ''] || '📋'}</span>
                <div className="text-left">
                  <h3 className="font-semibold">{cat.name}</h3>
                  {cat.description && <p className="text-xs text-gray-500">{cat.description}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{answered}/{qs.length}</span>
                <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full transition-all"
                    style={{ width: `${qs.length ? (answered / qs.length * 100) : 0}%` }}
                  />
                </div>
                <span className={`text-sm transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
              </div>
            </button>

            {isExpanded && (
              <div className="border-t divide-y">
                {qs.map((q, idx) => {
                  const existing = answerMap.get(q.id)
                  const isEditing = editingQ === q.id

                  return (
                    <div key={q.id} className="p-4">
                      <div className="flex items-start gap-3">
                        <span className="text-sm font-mono text-gray-400 mt-0.5 w-6 shrink-0">
                          {idx + 1}.
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm mb-1">{q.question_text}</p>
                          {q.hint && <p className="text-xs text-gray-400 mb-2">{q.hint}</p>}

                          {isEditing ? (
                            <div className="mt-2 space-y-2">
                              <textarea
                                value={draft}
                                onChange={e => setDraft(e.target.value)}
                                className="input text-sm min-h-[80px] resize-y"
                                placeholder="Type your notes from this conversation..."
                                autoFocus
                              />
                              <div className="flex gap-2">
                                <button onClick={() => handleSave(q.id)} disabled={saving} className="btn-primary text-sm py-1">
                                  {saving ? 'Saving...' : 'Save'}
                                </button>
                                <button onClick={() => setEditingQ(null)} className="btn-secondary text-sm py-1">
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : existing?.answer ? (
                            <div className="mt-1">
                              <p className="text-sm text-gray-700 bg-gray-50 rounded p-2">{existing.answer}</p>
                              <button onClick={() => startEdit(q)} className="text-xs text-brand-600 mt-1 hover:underline">
                                Edit
                              </button>
                            </div>
                          ) : (
                            <button onClick={() => startEdit(q)} className="text-xs text-brand-600 hover:underline mt-1">
                              Add answer
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
